/**
 * InstrumentActionSheet.tsx
 * Mobile bottom sheet for instrument actions (View, Edit, Duplicate, Archive/Restore, Delete)
 *
 * Used on mobile devices when a user taps a row in the UserInstrumentsTab grid.
 * Provides quick access to instrument actions with large touch targets.
 */

import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button
} from '@enfomentis/ui';
import { Eye, Edit, Copy, Archive, RotateCcw, Trash2 } from 'lucide-react';
import type { Instrument } from '@/types/schema/Instrument';

/**
 * Props for the InstrumentActionSheet component
 */
export interface InstrumentActionSheetProps {
  /** Whether the action sheet is open */
  isOpen: boolean;
  /** Handler for closing the action sheet */
  onClose: () => void;
  /** The selected instrument (null if none selected) */
  instrument: Instrument | null;
  /** Handler for viewing the instrument (read-only) */
  onView: (instrument: Instrument) => void;
  /** Handler for editing the instrument */
  onEdit: (instrument: Instrument) => void;
  /** Handler for duplicating the instrument */
  onDuplicate: (instrument: Instrument) => void;
  /** Handler for archiving the instrument */
  onArchive: (instrument: Instrument) => void;
  /** Handler for restoring an archived instrument */
  onRestore: (instrument: Instrument) => void;
  /** Handler for deleting the instrument */
  onDelete: (instrument: Instrument) => void;
  /** Whether the instrument is archived (shows Restore instead of Archive) */
  isArchived?: boolean;
}

/**
 * Mobile bottom sheet for instrument actions
 */
export const InstrumentActionSheet: React.FC<InstrumentActionSheetProps> = ({
  isOpen,
  onClose,
  instrument,
  onView,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  onDelete,
  isArchived = false
}) => {
  if (!instrument) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      placement="bottom"
    >
      <DrawerContent>
        {(_onClose) => (
          <>
            <DrawerHeader className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold">{instrument.symbol}</h2>
              <p className="text-small text-foreground-500">{instrument.displayName}</p>
            </DrawerHeader>
            <DrawerBody>
              <div className="flex flex-col gap-3">
                <Button
                  size="md"
                  variant="bordered"
                  color="primary"
                  startContent={<Eye size={20} />}
                  onPress={() => onView(instrument)}
                  className="justify-start"
                >
                  View Details
                </Button>
                <Button
                  size="md"
                  variant="bordered"
                  color="default"
                  startContent={<Edit size={20} />}
                  onPress={() => onEdit(instrument)}
                  className="justify-start"
                >
                  Edit Instrument
                </Button>
                <Button
                  size="md"
                  variant="bordered"
                  color="default"
                  startContent={<Copy size={20} />}
                  onPress={() => onDuplicate(instrument)}
                  className="justify-start"
                >
                  Duplicate
                </Button>
                {isArchived ? (
                  <Button
                    size="md"
                    variant="flat"
                    color="primary"
                    startContent={<RotateCcw size={20} />}
                    onPress={() => onRestore(instrument)}
                    className="justify-start"
                  >
                    Restore Instrument
                  </Button>
                ) : (
                  <Button
                    size="md"
                    variant="flat"
                    color="warning"
                    startContent={<Archive size={20} />}
                    onPress={() => onArchive(instrument)}
                    className="justify-start"
                  >
                    Archive Instrument
                  </Button>
                )}
                <Button
                  size="md"
                  variant="flat"
                  color="danger"
                  startContent={<Trash2 size={20} />}
                  onPress={() => onDelete(instrument)}
                  className="justify-start"
                >
                  Delete Instrument
                </Button>
              </div>
            </DrawerBody>
            <DrawerFooter>
              <Button
                size="md"
                variant="light"
                onPress={_onClose}
                className="w-full"
              >
                Cancel
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};
